import { useEffect, useState } from 'react';
import {
  DataTable,
  EmptyState,
  Icon,
  Panel,
  Stat,
  Tag,
  useErrorDialog,
  type Column,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { api, ApiRequestError } from '../../../lib/api.ts';
import { STAMP, formatPoints } from '../format.ts';
import { sentence } from '../vocabulary.ts';
import type { PointsEntry, PointsLedger } from '../types.ts';

/**
 * The points ledger for this player.
 *
 * The tab used to say "The points ledger is not on this route" over an endpoint
 * that has been live behind `players.view` the whole time:
 * `GET /api/points/players/:id` returns points.points_ledger for one player.
 * The notice was untrue, and an untrue notice is worse than an empty table —
 * an operator reads it and stops looking.
 *
 * TWO OF THE DESIGN'S FOUR TILES STILL HAVE NO SOURCE, and they are named
 * rather than filled: `points_ledger` carries no expiry column, so "expiring in
 * 30d" cannot be computed from anything; and neither a points-to-peso rate nor
 * a tier multiplier is stored as a typed value anywhere in this schema, so
 * "worth ₱1,824" and "at 2.5× VIP 4" would be numbers this console invented.
 * Points are therefore printed as a COUNT and never formatted as money.
 */
const columns: Column<PointsEntry>[] = [
  {
    key: 'when',
    header: 'When',
    render: (e) => (
      <span className="text-muted tabular">{STAMP.format(new Date(e.created_at))}</span>
    ),
  },
  {
    key: 'reason',
    header: 'Reason',
    // The stored code, sentence-cased. It is a free text column with no
    // reference table, so it is shown as stored rather than mapped through a
    // vocabulary that could silently drop a value nobody added to it.
    render: (e) =>
      e.reason ? (
        <>
          {sentence(e.reason)}
          {e.source_ref ? (
            <div className="text-subtle tabular" style={{ fontSize: '11px' }}>
              {e.source_ref}
            </div>
          ) : null}
        </>
      ) : (
        <span className="text-subtle">—</span>
      ),
  },
  {
    key: 'points',
    header: 'Points',
    numeric: true,
    render: (e) => {
      const value = BigInt(e.points);
      return (
        <Tag tone={value >= 0n ? 'neutral' : 'outline'}>
          {value >= 0n ? '+' : ''}
          {formatPoints(e.points)}
        </Tag>
      );
    },
  },
];

export function PointsTab({ playerId, playerRef }: { playerId: string; playerRef: string }) {
  const { showError } = useErrorDialog();
  const [ledger, setLedger] = useState<PointsLedger | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<PointsLedger>(`/api/points/players/${encodeURIComponent(playerId)}`)
      .then((d) => {
        if (cancelled) return;
        setLedger(d);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setLedger(null);
        setFailed(true);
        showError({
          title: 'Could not load the points ledger',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The read did not complete. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [playerId, showError]);

  if (loading && !ledger) {
    return (
      <Panel title="Points">
        <div className="skeleton" style={{ height: '240px' }}>
          &nbsp;
        </div>
      </Panel>
    );
  }

  if (failed || !ledger) {
    return (
      <Panel title="Points">
        <EmptyState icon="warning-circle" title="The points ledger could not be read">
          This panel is empty because the points ledger could not be fetched, not because the
          player has no points. The message that opened carries the reason.
        </EmptyState>
      </Panel>
    );
  }

  return (
    <Panel title="Points">
      <div className="stat-grid">
        <Stat
          label="Balance"
          value={formatPoints(ledger.balance)}
          delta="summed over the whole ledger, not over the rows below"
        />
        <Stat
          label="Earned lifetime"
          value={formatPoints(ledger.earned)}
          delta="every positive entry"
        />
        <Stat
          label="Redeemed"
          // Already negative on the wire — sum(points) FILTER (WHERE points < 0)
          // — so it is printed as stored rather than re-signed here.
          value={formatPoints(ledger.spent)}
          delta="every negative entry; a refused redemption re-credits and shows as a separate positive"
        />
        <Stat
          label="Entries"
          value={formatCount(ledger.entryCount)}
          delta={
            ledger.truncated
              ? `newest ${formatCount(ledger.items.length)} shown — the list stops at 50`
              : 'all of them shown below'
          }
        />
      </div>

      <div style={{ marginTop: 'var(--space-4)' }}>
        <DataTable
          caption={`Points ledger for ${playerRef}, newest first`}
          columns={columns}
          rows={ledger.items}
          rowKey={(e) => e.id}
          empty={
            <EmptyState icon="coins" title="No points on this account">
              A real zero — the points ledger holds no row for
              this player. A player with no points is not an error and not a missing read: the
              balance above is 0 because there is nothing to sum.
            </EmptyState>
          }
        />
      </div>

      <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
        <Icon name="warning-circle" />
        <div>
          <strong>Two more tiles — points expiring and their peso worth — are absent, not
          zero.</strong> Points carry no expiry date, so
          &ldquo;expiring in 30d&rdquo; cannot be computed from anything this platform stores; and
          neither the points-to-peso rate (&ldquo;worth ₱1,824&rdquo;) nor the tier multiplier
          (&ldquo;at 2.5× VIP 4&rdquo;) is held as a typed value anywhere, so both would be figures
          this console invented. Points are printed as a count for that reason and never as money.
          Redemptions themselves — and the verdict on one — are on{' '}
          <strong>Points Mall</strong>, where the reward and the stock live.
        </div>
      </div>
    </Panel>
  );
}
